// The shop's own clauses (2026-09-24).
//
// Shannon Wang, Shop 53 Custom Tattoo (La Plata MD), replying to her welcome half an hour after
// signing up: "I need to add I hereby release any and all employees, agents or persons representing
// Shop 53 Tattoo and Art Studio ... Is that possible?" Chris: "let's deliver to the yes, and if this
// is specific enough to build it into settings in some way, or allow a shop to set their own
// language, that's just fine."
//
// `public_data.own_clauses` is `[{id, text}, …]` in the owner's words, written from the shop app's
// Settings → Waiver form → Your own clauses. The form draws each one as a box the client ticks
// before signing — after the service's clauses and the state's notices, before the terms — and posts
// `ownClauses: {<id>: true}`; the signed waiver prints each ticked one from the shop's row by that
// id (services/waiver-api, app.py). THE WORDS ARE SHOWN AS WRITTEN IN EITHER LANGUAGE: they are the
// shop's own, and nobody here may translate a release the shop chose word by word. This module is
// the only place that decides what the key means, the recognition.ts shape: the rest of the form
// asks it.

import type { Lang } from "./i18n";

export type OwnClause = { id: string; text: string };

/** At most this many, the API's MAX_OWN_CLAUSES; a row that somehow carries more shows the first. */
export const MAX_OWN_CLAUSES = 5;

const ID = /^oc_[0-9a-f]{10}$/;

/** The clauses a shop's config carries, read tolerantly: a junk item is skipped, never fatal, so a
 *  hand-written row can never blank a form. Absent, null or anything but a list is none, which is
 *  every shop that has not written one. */
export function ownClausesOf(value: unknown): OwnClause[] {
  if (!Array.isArray(value)) return [];
  const out: OwnClause[] = [];
  const seen = new Set<string>();
  for (const item of value) {
    if (!item || typeof item !== "object") continue;
    const id = `${(item as { id?: unknown }).id ?? ""}`.trim();
    const text = `${(item as { text?: unknown }).text ?? ""}`.split(/\s+/).join(" ").trim();
    if (!ID.test(id) || !text || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, text });
    if (out.length === MAX_OWN_CLAUSES) break;
  }
  return out;
}

/** The first few words of a clause, for the sentence that names the box a client missed. */
export function ownClauseOpening(text: string, words = 8): string {
  const parts = text.split(" ");
  const opening = parts.slice(0, words).join(" ");
  return parts.length > words ? `${opening}…` : opening;
}

/** What the form says when a clause is left unticked: the ackError shape, naming the box by its
 *  opening words so a client with five boxes knows which one. */
export function ownClauseError(clause: OwnClause, lang: Lang = "en"): string {
  const opening = ownClauseOpening(clause.text);
  return lang === "es"
    ? `Debes marcar la casilla que empieza con “${opening}” para firmar.`
    : `You must tick the box that starts “${opening}” to sign.`;
}
